import { Signal } from '@verlo/shared/components/ui/signal';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import {
  Dialog, DialogContent, DialogBody, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@verlo/shared/components/ui/dialog';
import { useState } from 'react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Check, Cake, Heart, Briefcase, Star } from 'lucide-react';
import BookingStatusActions, { STATUS_CONFIG } from './BookingStatusActions';
import { turnTimeFor } from '../../lib/bookingQueue';

// The create / edit booking dialog, moved out of ReservationsPage.jsx as it was (B1).
// The page owns the form state and the save; this owns the fields.

export const OCCASIONS = [
  { value: 'none', label: 'None', icon: null },
  { value: 'birthday', label: 'Birthday', icon: Cake },
  { value: 'anniversary', label: 'Anniversary', icon: Heart },
  { value: 'business', label: 'Business', icon: Briefcase },
  { value: 'date_night', label: 'Date Night', icon: Heart },
  { value: 'celebration', label: 'Celebration', icon: Star },
  { value: 'other', label: 'Other', icon: Star },
];

// 10-minute slots, 12:00–23:00.
export const TIME_SLOTS = [];
for (let hour = 12; hour < 23; hour += 1) {
  for (let minute = 0; minute < 60; minute += 10) {
    TIME_SLOTS.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
  }
}

const BookingFormDialog = ({
  open, onOpenChange, dateChoices, formatDate, formData, setFormData, formErrors, setFormErrors,
  editingReservation, tables, turnTimes, suggestTable, getTableSlotStatus,
  conflictWarnings, setConflictWarnings, onSubmit, onCancelBooking, onChanged, slotGone,
}) => {
  const occasions = OCCASIONS;
  // The fields a host rarely touches at the door sit behind "More", so the form fits the
  // till's screen with Create in view. They open on their own when editing a booking that
  // has them.
  const hasMore = Boolean(formData.customer_email || (formData.occasion && formData.occasion !== 'none') || formData.notes);
  const [more, setMore] = useState(false);
  const showMore = more || hasMore;
  const dirty = Boolean(formData.customer_name || formData.notes);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="form"
        onInteractOutside={(e) => { if (dirty) e.preventDefault(); }}
        data-testid="booking-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {editingReservation ? 'Edit Reservation' : 'New Reservation'}
          </DialogTitle>
          <DialogDescription>
            {formatDate(formData.date)} at {formData.time || 'Select time'}
          </DialogDescription>
        </DialogHeader>
    
        <DialogBody className="grid gap-4 py-1">
          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Guest Name *</Label>
              <Input
                placeholder="e.g. John Smith"
                value={formData.customer_name}
                onChange={(e) => {
                  setFormData({ ...formData, customer_name: e.target.value });
                  if (formErrors.customer_name) setFormErrors({ ...formErrors, customer_name: false });
                }}
                className={formErrors.customer_name ? 'border-signal-stop-line focus:border-signal-stop-line focus:ring-signal-stop-line' : ''}
                data-testid="guest-name-input"
              />
              {formErrors.customer_name && (
                <p className="text-xs text-signal-stop">Guest name is required</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                placeholder="+44 7700 900000"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              />
            </div>
          </div>
      
          {/* Booking Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Select value={formData.date} onValueChange={(v) => setFormData({ ...formData, date: v })}>
                <SelectTrigger data-testid="booking-date-select"><SelectValue placeholder="Select date" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {dateChoices.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Party Size *</Label>
              <Select
                value={formData.party_size.toString()}
                onValueChange={(v) => {
                  const size = parseInt(v);
                  const suggested = suggestTable(size, formData.time);
                  setFormData({ 
                    ...formData, 
                    party_size: size,
                    table_id: suggested?.id || formData.table_id,
                    // Auto follows the party size; a duration the host chose stays.
                    duration_minutes: formData.duration_minutes,
                  });
                }}
              >
                <SelectTrigger data-testid="party-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5,6,7,8,10,12].map(n => (
                    <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? 'guest' : 'guests'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
        
            <div className="space-y-2">
              <Label>Time *</Label>
              <Select
                value={formData.time}
                onValueChange={(v) => {
                  const suggested = suggestTable(formData.party_size, v);
                  setFormData({ ...formData, time: v, table_id: suggested?.id || '' });
                  if (formErrors.time) setFormErrors({ ...formErrors, time: false });
                }}
              >
                <SelectTrigger 
                  data-testid="time-select"
                  className={formErrors.time ? 'border-signal-stop-line focus:border-signal-stop-line focus:ring-signal-stop-line' : ''}
                >
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {/* Times that have gone are there but greyed: a new booking is for later. */}
                  {TIME_SLOTS.map(slot => (
                    <SelectItem key={slot} value={slot} disabled={Boolean(slotGone?.(slot))}>{slot}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formErrors.time && (
                <p className="text-xs text-signal-stop">Time is required</p>
              )}
            </div>
        
            <div className="space-y-2">
              <Label>Duration</Label>
              <Select
                value={formData.duration_minutes ? String(formData.duration_minutes) : 'auto'}
                onValueChange={(v) => setFormData({ ...formData, duration_minutes: v === 'auto' ? null : parseInt(v, 10) })}
              >
                <SelectTrigger data-testid="duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto · {turnTimeFor(turnTimes, formData.party_size)} min for {formData.party_size}</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                  <SelectItem value="150">2.5 hours</SelectItem>
                  <SelectItem value="180">3 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
      
          {/* Table Assignment */}
          <div className="space-y-2">
            <Label>Table Assignment</Label>
            <Select
              value={formData.table_id}
              onValueChange={(v) => setFormData({ ...formData, table_id: v })}
            >
              <SelectTrigger data-testid="table-select">
                <SelectValue placeholder="Auto-assign or select table" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-assign later</SelectItem>
                {tables
                  .filter(t => t.capacity >= formData.party_size)
                  .map(t => {
                    const slotStatus = formData.time ? getTableSlotStatus(t, formData.time) : { status: 'available' };
                    const isAvailable = slotStatus.status === 'available';
                    return (
                      <SelectItem 
                        key={t.id} 
                        value={t.id}
                        disabled={!isAvailable}
                      >
                        {t.name} (seats {t.capacity}) {!isAvailable && `- ${slotStatus.reason || slotStatus.status}`}
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
            {formData.table_id && formData.table_id !== 'auto' && (
              <p className="text-xs text-signal-ok flex items-center gap-1">
                <Check size={12} />
                {tables.find(t => t.id === formData.table_id)?.name} selected
              </p>
            )}
          </div>
      
          {!showMore && (
            <button type="button" className="justify-self-start text-[13px] font-semibold text-verlo-hover hover:underline" onClick={() => setMore(true)} data-testid="booking-more-btn">
              More: email, occasion, notes
            </button>
          )}
          {showMore && (<>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              placeholder="guest@email.com"
              value={formData.customer_email}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
            />
          </div>

          {/* Occasion */}
          <div className="space-y-2">
            <Label>Occasion</Label>
            <Select
              value={formData.occasion}
              onValueChange={(v) => setFormData({ ...formData, occasion: v })}
            >
              <SelectTrigger data-testid="occasion-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {occasions.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    <span className="flex items-center gap-2">
                      {o.icon && <o.icon size={14} />}
                      {o.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.occasion === 'other' && (
              <Input
                placeholder="e.g. Mates birthday, Engagement"
                value={formData.occasion_notes}
                onChange={(e) => setFormData({ ...formData, occasion_notes: e.target.value })}
                className="mt-2"
              />
            )}
          </div>
      
          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              placeholder="Any special requests or notes..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="min-h-[60px]"
            />
          </div>
          </>)}
        </DialogBody>

        {/* Conflict warnings — above the buttons, where the eye is when it taps Create. */}
        {conflictWarnings.length > 0 && (
          <Notice tone="warn" word="Clash" title="Another booking has this slot" className="shrink-0" data-testid="conflict-warning">
            {conflictWarnings.map(c => (
              <p key={c.id}>
                {c.customer_name} at {c.time} ({c.party_size} guests) · {c.table_name || 'Unassigned'}
              </p>
            ))}
          </Notice>
        )}

        <DialogFooter className="gap-2">
          {editingReservation && (
            <>
              {/* `BookingStatusActions` renders nothing for completed / cancelled /
                  no-show — action buttons for a status with no next step — so this
                  is otherwise the only place the dialog shows status at all. */}
              <div className="flex items-center gap-1.5 mr-2">
                <span className="text-[12px] text-ink-soft">Status</span>
                {(() => {
                  const sc = STATUS_CONFIG[editingReservation.status] || STATUS_CONFIG.pending;
                  return sc.tone
                    ? <Signal tone={sc.tone}>{sc.label}</Signal>
                    : <span className="text-[12px] font-medium text-ink-soft">{sc.label}</span>;
                })()}
              </div>
              <BookingStatusActions
                reservation={editingReservation}
                onStatusChange={() => { onOpenChange(false); onChanged(); }}
              />
              <div className="flex-1" />
              <Button 
                variant="destructive" 
                onClick={() => onCancelBooking(editingReservation.id)}
              >
                Cancel Booking
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => { onOpenChange(false); setConflictWarnings([]); }}>
            Close
          </Button>
          <Button onClick={onSubmit} className="bg-verlo hover:bg-verlo-hover">
            {editingReservation ? 'Update' : 'Create'} Reservation
          </Button>
        </DialogFooter>
    
      </DialogContent>
    </Dialog>
  );
};

export default BookingFormDialog;
